import React, { useState, useEffect } from 'react';
import { EditButton, DeleteButton, Edit, Create, SimpleForm, TextInput, BooleanInput, DateField } from 'react-admin';
import { Link } from 'react-router-dom';
import SortableTable from './components/SortableTable';
import { httpClient, publicBase } from './dataProvider';

export const ApprovedTrainingUserList = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchUsers = () => {
        const url = `${publicBase}/admin/approved-training-users?limit=1000`;
        httpClient(url)
            .then(({ json }) => {
                setUsers(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching approved training users:', error);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleDelete = async (record) => {
        if (window.confirm(`Are you sure you want to remove access for ${record.email}?`)) {
            try {
                await httpClient(`${publicBase}/admin/approved-training-users/${record.id}`, {
                    method: 'DELETE'
                });
                // Refresh the list
                fetchUsers();
            } catch (error) {
                console.error('Error deleting approved user:', error);
                alert('Failed to remove user access');
            }
        }
    };

    const columns = [
        { key: 'email', header: 'Email' },
        { key: 'approved_by', header: 'Approved By' },
        {
            key: 'approved_at',
            header: 'Approved Date',
            render: (record) => new Date(record.approved_at).toLocaleDateString()
        },
        {
            key: 'is_active',
            header: 'Active',
            render: (record) => record.is_active ? '✓' : '✗'
        },
        { key: 'notes', header: 'Notes' },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <button
                    onClick={() => handleDelete(record)}
                    style={{
                        padding: '4px 8px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Remove Access
                </button>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Approved Training Users</h2>
                <Link
                    to="/approved-training-users/create"
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px'
                    }}
                >
                    Add Approved User
                </Link>
            </div>
            <SortableTable
                data={users}
                columns={columns}
                defaultSort={{ key: 'approved_at', direction: 'desc' }}
            />
        </div>
    );
};

export const ApprovedTrainingUserCreate = () => {
    const [email, setEmail] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            alert('Email is required');
            return;
        }

        setLoading(true);
        try {
            await httpClient(`${publicBase}/admin/approved-training-users`, {
                method: 'POST',
                body: JSON.stringify({
                    email: email.trim().toLowerCase(),
                    notes: notes.trim() || null,
                    approved_by: 'admin' // This will be set by backend from X-User-Id header
                })
            });

            alert('User approved successfully!');
            // Reset form
            setEmail('');
            setNotes('');
        } catch (error) {
            console.error('Error creating approved user:', error);
            if (error.body?.detail?.includes('already approved')) {
                alert('This email is already approved for training access');
            } else {
                alert('Failed to approve user');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
            <h2>Add Approved Training User</h2>
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                        Email Address *
                    </label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                        placeholder="user@company.com"
                    />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                        Notes (Optional)
                    </label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            resize: 'vertical'
                        }}
                        placeholder="Company, role, or other relevant information..."
                    />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: loading ? '#ccc' : '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {loading ? 'Adding...' : 'Add Approved User'}
                    </button>

                    <Link
                        to="/approved-training-users"
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#666',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px'
                        }}
                    >
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    );
};
